<template>
  <div class="mt-4">
    <v-alert v-if="lastError" dense type="error" outlined dismissible class="mx-auto mb-2" max-width="95%"
      @input="lastError = ''">
      <strong>API 호출 실패:</strong>
      <div style="font-size:11px;white-space:pre-wrap;">{{ lastError }}</div>
    </v-alert>
    <v-card class="mx-auto" max-width="95%" elevation="2">
      <v-toolbar dark dense color="indigo darken-2">
        <v-toolbar-title>
          <v-icon left small>mdi-calendar-range</v-icon>일별 통계
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-tabs v-model="tab" dark dense background-color="transparent" slider-color="amber">
          <v-tab>매체별</v-tab>
          <v-tab>게임별</v-tab>
        </v-tabs>
      </v-toolbar>
      <v-card-text>
        <v-row dense>
          <v-col cols="6" md="3">
            <v-text-field dense outlined hide-details type="date" label="From" v-model="fromDate"></v-text-field>
          </v-col>
          <v-col cols="6" md="3">
            <v-text-field dense outlined hide-details type="date" label="To" v-model="toDate"></v-text-field>
          </v-col>
          <v-col cols="6" md="3" v-if="tab===0">
            <v-autocomplete dense outlined hide-details clearable
              label="매체 (옵션)"
              :items="mediaItems" :loading="loadingMedia"
              item-text="label" item-value="app_key"
              v-model="filterAppKey"
              no-data-text="매체 없음"
              placeholder="검색 가능"></v-autocomplete>
          </v-col>
          <v-col cols="6" md="3" v-if="tab===1">
            <v-autocomplete dense outlined hide-details clearable
              label="게임 (옵션)"
              :items="gameItems" :loading="loadingGames"
              item-text="label" item-value="game_id"
              v-model="filterGameId"
              no-data-text="게임 없음"
              placeholder="검색 가능"></v-autocomplete>
          </v-col>
          <v-col cols="6" md="3">
            <v-btn color="primary" :loading="loading" @click="load" block>조회</v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 트렌드 차트 -->
    <v-card class="mx-auto mt-4" max-width="95%" elevation="2">
      <v-toolbar dark dense color="teal darken-2">
        <v-toolbar-title>일별 추이 ({{ chartMetric }})</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn-toggle v-model="chartMetric" mandatory dense color="amber accent-2">
          <v-btn x-small value="total_bet_amount" dark>베팅금</v-btn>
          <v-btn x-small value="bet_count" dark>베팅수</v-btn>
          <v-btn x-small value="active_user_count" dark v-if="tab===0">DAU</v-btn>
          <v-btn x-small value="participant_count" dark v-if="tab===1">참여자</v-btn>
        </v-btn-toggle>
      </v-toolbar>
      <v-card-text>
        <line-chart v-if="chartData.datasets.length" :chart-data="chartData" :options="chartOptions" :height="80"></line-chart>
        <div v-else class="text-center grey--text pa-6">데이터 없음</div>
      </v-card-text>
    </v-card>

    <!-- 매체별 테이블 -->
    <v-card v-if="tab===0" class="mx-auto mt-4" max-width="95%" elevation="2">
      <v-toolbar dark dense color="indigo">
        <v-toolbar-title>매체별 일자 데이터</v-toolbar-title>
      </v-toolbar>
      <v-data-table dense :headers="mediaHeaders" :items="mediaRows" :items-per-page="50" :loading="loading">
        <template v-slot:item.total_bet_amount="{ item }">{{ fmt(item.total_bet_amount) }}</template>
        <template v-slot:item.total_payout_amount="{ item }">{{ fmt(item.total_payout_amount) }}</template>
        <template v-slot:item.net_revenue="{ item }">
          <span :class="item.net_revenue >= 0 ? 'green--text' : 'red--text'">{{ fmt(item.net_revenue) }}</span>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip x-small :color="statusColor(item.status)" dark>{{ item.status }}</v-chip>
        </template>
      </v-data-table>
    </v-card>

    <!-- 게임별 테이블 -->
    <v-card v-if="tab===1" class="mx-auto mt-4" max-width="95%" elevation="2">
      <v-toolbar dark dense color="indigo">
        <v-toolbar-title>게임별 일자 데이터</v-toolbar-title>
      </v-toolbar>
      <v-data-table dense :headers="gameHeaders" :items="gameRows" :items-per-page="50" :loading="loading">
        <template v-slot:item.total_bet_amount="{ item }">{{ fmt(item.total_bet_amount) }}</template>
        <template v-slot:item.total_payout="{ item }">{{ fmt(item.total_payout) }}</template>
        <template v-slot:item.margin_rate_calc="{ item }">
          <v-chip x-small :color="marginColor(marginRate(item))" dark>{{ pctOrDash(marginRate(item)) }}</v-chip>
        </template>
        <template v-slot:item.refund_round_rate="{ item }">
          <v-chip x-small :color="refundColor(refundRate(item))" :dark="refundRate(item) > 0">
            {{ pctOrDash(refundRate(item)) }}
          </v-chip>
        </template>
      </v-data-table>
    </v-card>

    <div class="py-12"></div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import LineChart from './charts/LineChart.js';

function fmtDate(d) {
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

export default {
  name: 'Danta_Stats_Daily',
  components: { LineChart },
  data() {
    const today = new Date();
    const weekAgo = new Date(today.getTime() - 7 * 24 * 3600 * 1000);
    return {
      tab: 0,
      loading: false,
      lastError: '',
      fromDate: fmtDate(weekAgo),
      toDate:   fmtDate(today),
      filterAppKey: '',
      filterGameId: '',
      mediaRows: [],
      gameRows: [],
      mediaItems: [],
      gameItems: [],
      loadingMedia: false,
      loadingGames: false,
      chartMetric: 'total_bet_amount',
      mediaHeaders: [
        { text: '일자', value: 'stat_date' },
        { text: '매체ID', value: 'media_id' },
        { text: 'app_key', value: 'app_key' },
        { text: 'DAU', value: 'active_user_count', align: 'right' },
        { text: '신규', value: 'new_user_count', align: 'right' },
        { text: '베팅', value: 'bet_count', align: 'right' },
        { text: '취소', value: 'cancelled_count', align: 'right' },
        { text: '베팅금', value: 'total_bet_amount', align: 'right' },
        { text: '지급액', value: 'total_payout_amount', align: 'right' },
        { text: '순수익', value: 'net_revenue', align: 'right' },
        { text: '상태', value: 'status' },
      ],
      gameHeaders: [
        { text: '일자', value: 'stat_date' },
        { text: '게임ID', value: 'game_id', align: 'right' },
        { text: '게임명', value: 'game_title' },
        { text: '템플릿', value: 'template_code' },
        { text: '라운드', value: 'round_count', align: 'right' },
        { text: '참여자', value: 'participant_count', align: 'right' },
        { text: '베팅수', value: 'bet_count', align: 'right' },
        { text: '베팅금', value: 'total_bet_amount', align: 'right' },
        { text: '지급액', value: 'total_payout', align: 'right' },
        { text: '마진율', value: 'margin_rate_calc', align: 'right' },        // 클라이언트 계산
        { text: 'UP승', value: 'up_win_count', align: 'right' },
        { text: 'DOWN승', value: 'down_win_count', align: 'right' },
        { text: 'SAME', value: 'same_count', align: 'right' },
        { text: '취소', value: 'cancelled_count', align: 'right' },
        { text: '환불%', value: 'refund_round_rate', align: 'right' },        // (same+cancelled)/round
      ],
    };
  },
  computed: {
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: { yAxes: [{ ticks: { beginAtZero: true } }] },
        legend: { display: true },
      };
    },
    chartData() {
      const rows = (this.tab === 0 ? this.mediaRows : this.gameRows).slice().sort((a, b) => String(a.stat_date).localeCompare(String(b.stat_date)));
      // 일자별 합계 (매체/게임 합산) 단순 추이
      const byDate = new Map();
      for (const r of rows) {
        const d = String(r.stat_date).substring(0, 10);
        const v = Number(r[this.chartMetric] || 0);
        byDate.set(d, (byDate.get(d) || 0) + v);
      }
      const labels = Array.from(byDate.keys());
      const values = labels.map(l => byDate.get(l));
      if (labels.length === 0) return { labels: [], datasets: [] };
      return {
        labels,
        datasets: [{
          label: this.chartMetric,
          backgroundColor: 'rgba(63, 81, 181, 0.18)',
          borderColor: '#3f51b5',
          data: values,
          fill: true,
        }],
      };
    },
  },
  created() {
    this.load();
    this.loadMediaList();
    this.loadGameList();
  },
  methods: {
    ...mapActions(['DANTA_STATS_DAILY_MEDIA', 'DANTA_STATS_DAILY_GAMES', 'DANTA_GET_MEDIA_LIST', 'DANTA_GET_GAME_LIST']),
    fmt(n) { return n == null ? '-' : Number(n).toLocaleString(); },
    pctOrDash(rate) {
      if (rate == null) return '-';
      return `${(Number(rate) * 100).toFixed(1)}%`;
    },
    // 단순 마진율 = (베팅금 - 지급액) / 베팅금. 환불 포함이라 환불 위주 게임은 0% 근처.
    //   "실효 마진율" (환불 제외) 은 daily_game_stats 에 settled 컬럼 없어서 today 화면에서만 가능.
    marginRate(item) {
      const bet = Number(item.total_bet_amount || 0);
      if (!bet) return null;
      const pay = Number(item.total_payout || 0);
      return (bet - pay) / bet;
    },
    // 환불 라운드 비율 = (same + cancelled) / round_count
    refundRate(item) {
      const r = Number(item.round_count || 0);
      if (!r) return null;
      return (Number(item.same_count || 0) + Number(item.cancelled_count || 0)) / r;
    },
    marginColor(m) {
      if (m == null) return 'grey';
      if (m < 0)     return 'red';     // 운영자 손해
      if (m < 0.01)  return 'orange';  // 1% 미만 — 환불 위주 의심
      return 'green';
    },
    refundColor(r) {
      if (r == null || r === 0) return 'grey lighten-2';
      if (r >= 0.5)              return 'red';     // 절반 이상 환불
      if (r >= 0.2)              return 'orange';
      return 'amber';
    },
    statusColor(s) { return s === 'completed' ? 'green' : s === 'failed' ? 'red' : 'grey'; },
    async loadMediaList() {
      this.loadingMedia = true;
      try {
        const r = await this.DANTA_GET_MEDIA_LIST({ client_id: null });
        const rows = (r && r.media_list) || [];
        this.mediaItems = rows.map(m => ({
          app_key: m.app_key,
          label: `${m.media_name || m.media_id || m.app_key}  [${m.app_key}]`,
        }));
      } catch (err) {
        console.warn('media list load 실패:', err);
      } finally { this.loadingMedia = false; }
    },
    async loadGameList() {
      this.loadingGames = true;
      try {
        const r = await this.DANTA_GET_GAME_LIST({ page: 1, page_size: 500 });
        const rows = (r && r.game_list) || [];
        this.gameItems = rows.map(g => ({
          game_id: Number(g.game_id),
          label: `${g.title || g.game_code || '(이름없음)'}  [#${g.game_id}]`,
        }));
      } catch (err) {
        console.warn('game list load 실패:', err);
      } finally { this.loadingGames = false; }
    },
    async load() {
      this.loading = true;
      try {
        if (this.tab === 0) {
          const r = await this.DANTA_STATS_DAILY_MEDIA({
            from: this.fromDate, to: this.toDate,
            app_key: this.filterAppKey || undefined,
          });
          this.mediaRows = (r && r.rows) || [];
        } else {
          const r = await this.DANTA_STATS_DAILY_GAMES({
            from: this.fromDate, to: this.toDate,
            game_id: this.filterGameId || undefined,
          });
          this.gameRows = (r && r.rows) || [];
        }
        this.lastError = '';
      } catch (err) {
        this.lastError = (err && (err.message || err.err_message)) || String(err);
        console.error('[Stats_Daily] load 실패:', err);
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    tab() { this.load(); },
  },
};
</script>
